import { useEffect, useState } from "react";
import { BadgeDollarSign, CalendarPlus, Users, Download, FileText, ImagePlus, Images, Lock, LogOut, Megaphone, RefreshCw } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { SectionHeading } from "@/components/SectionHeading";
import { AdminItemList } from "@/components/AdminItemList";
import { SponsoredAdmin } from "@/components/SponsoredAdmin";
import { SponsorsAdmin } from "@/components/SponsorsAdmin";
import { HeroImagesAdmin } from "@/components/HeroImagesAdmin";
import { AutoSyncCard, UsersAdmin } from "@/components/UsersAdmin";
import { MediaMirrorCard } from "@/components/MediaMirrorCard";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { FALLBACK_IMAGE, parseSeries } from "@/utils/format";
import { ChronicleGalleryField } from "@/components/ChronicleGalleryField";
import { SocialPublishField } from "@/components/SocialPublishField";
import { PendingPublications } from "@/components/PendingPublications";
import { DateField } from "@/components/DateField";

const today = (() => { const now = new Date(); return [now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0")].join("-"); })();

const modalities = ["Foso Universal", "Trap 5", "Foso Olímpico"];
const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function parseFlickrAlbums(value) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, url] = line.split("|").map((part) => part.trim());
      return { label: label || "Álbum Flickr", location: label || null, url };
    })
    .filter((album) => album.url && album.url.startsWith("http"));
}

function ChroniclePhotoField({ form, update }) {
  const [uploading, setUploading] = useState(false);
  const [albums, setAlbums] = useState(null);
  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/chronicles/photo", body, { headers: { "Content-Type": "multipart/form-data" } });
      update("image", data.url);
      toast.success("Fotografía subida");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploading(false);
    }
  };
  const searchFlickr = async () => {
    try {
      const { data } = await api.get("/admin/flickr/match", { params: { date: form.date, location: form.location } });
      setAlbums(data);
      if (!data.length) toast.info("No hay álbumes de Flickr con esa fecha y localidad");
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };
  return (
    <div className="grid gap-3 border border-white/10 bg-white/[0.02] p-4" data-testid="admin-chronicle-photo-field">
      <span className="field-label">Fotografía de los premiados (opcional)</span>
      <div className="flex flex-wrap items-center gap-3">
        {form.image ? <img src={form.image.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image}` : form.image} alt="Fotografía elegida" className="h-20 w-28 object-cover" data-testid="admin-chronicle-photo-preview" /> : <span className="grid h-20 w-28 place-items-center bg-charcoal font-mono text-[9px] uppercase text-slate-500">Sin foto</span>}
        <label className="inline-flex cursor-pointer items-center gap-2 border border-clay/40 bg-clay/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-clay hover:bg-clay hover:text-charcoal">
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" data-testid="admin-chronicle-photo-upload" />
          {uploading ? "Subiendo..." : "Subir desde el ordenador"} <ImagePlus className="h-3 w-3" />
        </label>
        <Button type="button" variant="outline" onClick={searchFlickr} disabled={!form.date} className="rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.2em]" data-testid="admin-chronicle-flickr-search">Buscar en Flickr por fecha</Button>
        {form.image ? <button type="button" onClick={() => update("image", "")} className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-red-300" data-testid="admin-chronicle-photo-clear">Quitar</button> : null}
      </div>
      {albums?.length ? (
        <div className="flex flex-wrap gap-2" data-testid="admin-chronicle-flickr-covers">
          {albums.map((album) => <button key={album.album_id || album.url} type="button" onClick={() => update("image", album.cover_url)} className="border border-white/10 hover:border-clay" title={album.label}><img src={album.cover_url} alt={album.label} className="h-16 w-24 object-cover" /><span className="block px-1 font-mono text-[8px] uppercase text-slate-400">{album.label}</span></button>)}
        </div>
      ) : null}
      <Input value={form.image} onChange={(event) => update("image", event.target.value)} placeholder="O pega un enlace: foto de Flickr (vale el enlace de la página) o imagen de Internet" className="h-10 rounded-none border-white/10 bg-charcoal text-xs text-slate-100 placeholder:text-slate-600" data-testid="admin-chronicle-image-input" />
    </div>
  );
}

function parseClassifications(value) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [name, category, seriesText] = line.split("|").map((part) => (part || "").trim());
      const series = String(seriesText || "").split(/[\s,]+/).map(Number).filter(Number.isFinite);
      return { position: index + 1, name, category: category || "Absoluta", series, score: series.reduce((total, item) => total + item, 0) };
    })
    .filter((row) => row.name);
}

function classificationsToText(rows) {
  return rows.map((row) => `${row.name} | ${row.category} | ${(row.series || []).join(" ")}`).join("\n");
}

function albumsToText(albums) {
  return albums.map((album) => `${album.label || album.location || "Álbum Flickr"} | ${album.url}`).join("\n");
}

export function AdminPanel() {
  const { user, loading, login, logout } = useAuth();

  return (
    <section id="redaccion" className="section-shell bg-panel/45" data-testid="admin-panel-section">
      <SectionHeading number="07" eyebrow="Redacción" title="Publicar, corregir y ordenar cada tirada" description="Acceso para la redacción y los colaboradores aprobados. ¿Aún no tienes cuenta? Solicítala en el apartado Registro." />
      {loading ? (
        <div className="poster-card p-8 text-slate-400" data-testid="admin-loading-state">Comprobando sesión...</div>
      ) : user ? (
        <AdminDashboard user={user} onLogout={logout} />
      ) : (
        <LoginCard onLogin={login} />
      )}
    </section>
  );
}

function LoginCard({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      await onLogin(form);
      toast.success("Sesión iniciada");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={submit} className="poster-card mx-auto max-w-xl p-7 sm:p-9" data-testid="admin-login-form">
      <div className="mb-7 flex items-center gap-4">
        <span className="grid h-12 w-12 place-items-center border border-clay/40 bg-clay/10 text-clay"><Lock className="h-5 w-5" /></span>
        <div>
          <h3 className="font-display text-4xl uppercase leading-none">Acceso redacción</h3>
          <p className="mt-2 text-sm text-slate-400">Redacción y colaboradores aprobados</p>
        </div>
      </div>
      <Field label="Correo" type="email" value={form.email} onChange={(value) => setForm({ ...form, email: value })} testId="admin-email-input" required />
      <Field label="Contraseña" type="password" value={form.password} onChange={(value) => setForm({ ...form, password: value })} testId="admin-password-input" required />
      {error ? <p className="mb-5 border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200" data-testid="admin-login-error">{error}</p> : null}
      <Button disabled={pending} className="h-12 w-full rounded-none bg-clay font-mono text-xs uppercase tracking-[0.22em] text-charcoal hover:bg-brass" data-testid="admin-login-submit-button">
        {pending ? "Entrando..." : "Entrar al panel"}
      </Button>
    </form>
  );
}

function AdminDashboard({ user, onLogout }) {
  const isStaff = user?.role === "admin" || user?.role === "editor";
  return (
    <div className="poster-card p-5 sm:p-8" data-testid="admin-dashboard">
      <div className="mb-8 flex flex-col justify-between gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-center">
        <div>
          <p className="eyebrow">Sesión activa</p>
          <h3 className="mt-2 font-display text-4xl uppercase leading-none">{user.name}</h3>
          <p className="mt-2 text-sm text-slate-400" data-testid="admin-user-email">{user.email}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <CalendarSyncButton />
          <FlickrSyncButton />
          <WordpressSyncButton />
          <Button type="button" onClick={onLogout} variant="outline" className="rounded-none border-white/15 bg-transparent font-mono text-xs uppercase tracking-[0.2em] hover:border-red-400/60 hover:bg-red-500/10 hover:text-red-200" data-testid="admin-logout-button">
            Cerrar sesión <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mb-8 grid gap-4">{user?.role === "admin" ? <><AutoSyncCard /><MediaMirrorCard /></> : null}</div>
      <PendingPublications user={user} />
      {!isStaff ? (
        <div className="mb-8 border border-clay/30 bg-clay/10 p-4 text-sm text-slate-200" data-testid="admin-collaborator-notice">
          Cuenta de colaborador: puedes publicar {[user.can_publish_posters && "carteles", user.can_publish_chronicles && "crónicas"].filter(Boolean).join(" y ") || "nada todavía"}. Tus publicaciones quedan pendientes hasta que la redacción las apruebe; recibirás un correo con el resultado.
        </div>
      ) : null}
      <Tabs defaultValue={isStaff ? "event" : "chronicle"} className="w-full">
        <TabsList className={`mb-8 grid h-auto w-full grid-cols-2 gap-2 rounded-none bg-charcoal p-2 ${isStaff ? "sm:grid-cols-4 lg:grid-cols-7" : "sm:grid-cols-1"}`} data-testid="admin-tabs">
          {isStaff ? <TabsTrigger value="event" className="rounded-none py-3 data-[state=active]:bg-clay data-[state=active]:text-charcoal" data-testid="admin-tab-event"><ImagePlus className="mr-2 h-4 w-4" />Evento</TabsTrigger> : null}
          <TabsTrigger value="chronicle" className="rounded-none py-3 data-[state=active]:bg-clay data-[state=active]:text-charcoal" data-testid="admin-tab-chronicle"><FileText className="mr-2 h-4 w-4" />Publicaciones</TabsTrigger>
          {isStaff ? <><TabsTrigger value="calendar" className="rounded-none py-3 data-[state=active]:bg-clay data-[state=active]:text-charcoal" data-testid="admin-tab-calendar"><CalendarPlus className="mr-2 h-4 w-4" />Calendario</TabsTrigger>
          <TabsTrigger value="sponsored" className="rounded-none py-3 data-[state=active]:bg-clay data-[state=active]:text-charcoal" data-testid="admin-tab-sponsored"><Megaphone className="mr-2 h-4 w-4" />Patrocinios</TabsTrigger>
          <TabsTrigger value="sponsors" className="rounded-none py-3 data-[state=active]:bg-clay data-[state=active]:text-charcoal" data-testid="admin-tab-sponsors"><BadgeDollarSign className="mr-2 h-4 w-4" />Logotipos</TabsTrigger>
          <TabsTrigger value="hero" className="rounded-none py-3 data-[state=active]:bg-clay data-[state=active]:text-charcoal" data-testid="admin-tab-hero"><ImagePlus className="mr-2 h-4 w-4" />Fotos portada</TabsTrigger></> : null}
          {user?.role === "admin" ? <TabsTrigger value="users" className="rounded-none py-3 data-[state=active]:bg-clay data-[state=active]:text-charcoal" data-testid="admin-tab-users"><Users className="mr-2 h-4 w-4" />Usuarios</TabsTrigger> : null}
        </TabsList>
        <TabsContent value="event"><EventForm /></TabsContent>
        <TabsContent value="chronicle"><ChronicleForm user={user} /></TabsContent>
        <TabsContent value="calendar"><CalendarForm /></TabsContent>
        <TabsContent value="sponsored"><SponsoredAdmin /></TabsContent>
        <TabsContent value="sponsors"><SponsorsAdmin /></TabsContent>
        <TabsContent value="hero"><HeroImagesAdmin /></TabsContent>
        <TabsContent value="users"><UsersAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

function usePublisher(endpoint, keys, successMessage) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const request = id ? api.put(`/admin/${endpoint}/${id}`, payload) : api.post(`/admin/${endpoint}`, payload);
      return (await request).data;
    },
    onSuccess: () => {
      toast.success(successMessage);
      keys.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    },
    onError: (error) => toast.error(formatApiError(error)),
  });
}

function CalendarSyncButton() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => (await api.post("/admin/calendar/sync")).data,
    onSuccess: (data) => {
      toast.success(`Calendario sincronizado: ${data.imported} tiradas`);
      queryClient.invalidateQueries({ queryKey: ["calendar"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  return (
    <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} variant="outline" className="rounded-none border-clay/40 bg-clay/10 font-mono text-xs uppercase tracking-[0.2em] text-clay hover:bg-clay hover:text-charcoal" data-testid="admin-calendar-sync-button">
      {mutation.isPending ? "Sincronizando..." : "Sincronizar Google Sheet"} <RefreshCw className={`h-4 w-4 ${mutation.isPending ? "animate-spin" : ""}`} />
    </Button>
  );
}

function FlickrSyncButton() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => (await api.post("/admin/flickr/sync")).data,
    onSuccess: (data) => {
      toast.success(`Flickr: ${data.imported} álbumes leídos, ${data.linked} enlazados${data.partial ? " (lectura parcial sin clave API)" : ""}`);
      ["archive", "archive-summary", "chronicles", "flickr-albums"].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  return (
    <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} variant="outline" className="rounded-none border-white/15 bg-white/[0.03] font-mono text-xs uppercase tracking-[0.2em] text-slate-100 hover:border-clay/60 hover:bg-clay/10 hover:text-clay" data-testid="admin-flickr-sync-button">
      {mutation.isPending ? "Leyendo Flickr..." : "Sincronizar Flickr"} <Images className={`h-4 w-4 ${mutation.isPending ? "animate-pulse" : ""}`} />
    </Button>
  );
}

function WordpressSyncButton() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => (await api.post("/admin/wordpress/sync")).data,
    onSuccess: (data) => {
      toast.success(`WordPress importado: ${data.imported} entradas`);
      queryClient.invalidateQueries({ queryKey: ["archive"] });
      queryClient.invalidateQueries({ queryKey: ["archive-summary"] });
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  return (
    <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} variant="outline" className="rounded-none border-white/15 bg-white/[0.03] font-mono text-xs uppercase tracking-[0.2em] text-slate-100 hover:border-clay/60 hover:bg-clay/10 hover:text-clay" data-testid="admin-wordpress-sync-button">
      {mutation.isPending ? "Importando..." : "Importar WordPress"} <Download className="h-4 w-4" />
    </Button>
  );
}

function EventForm() {
  const initial = { title: "", date: "2026-08-16", time: "09:00", location: "", modality: "Foso Universal", status: "Próxima", poster: FALLBACK_IMAGE, description: "", featured: false };
  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(null);
  const mutation = usePublisher("events", ["events", "stats"], editing ? "Evento actualizado" : "Evento publicado");
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (editing) {
      setForm({ ...editing, time: editing.time || "09:00", featured: Boolean(editing.featured) });
    }
  }, [editing]);

  const reset = () => {
    setEditing(null);
    setForm(initial);
  };

  const submit = (event) => {
    event.preventDefault();
    mutation.mutate({ id: editing?.id, payload: form }, { onSuccess: reset });
  };

  return (
    <>
      <form onSubmit={submit} className="admin-form" data-testid="admin-event-form">
        {editing ? <EditNotice title={editing.title} onCancel={reset} testId="admin-event-cancel-edit" /> : null}
        <Field label="Título" value={form.title} onChange={(value) => update("title", value)} testId="admin-event-title-input" required />
        <div className="grid gap-5 md:grid-cols-3">
          <DateField label="Fecha" value={form.date} onChange={(value) => update("date", value)} testId="admin-event-date-input" required />
          <Field label="Hora" type="time" value={form.time} onChange={(value) => update("time", value)} testId="admin-event-time-input" required />
          <SelectField label="Modalidad" value={form.modality} onChange={(value) => update("modality", value)} options={modalities} testId="admin-event-modality-select" />
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <Field label="Ubicación" value={form.location} onChange={(value) => update("location", value)} testId="admin-event-location-input" required />
          <Field label="Estado" value={form.status} onChange={(value) => update("status", value)} testId="admin-event-status-input" required />
        </div>
        <Field label="URL del cartel o fotografía" value={form.poster} onChange={(value) => update("poster", value)} testId="admin-event-poster-input" required />
        <TextareaField label="Descripción" value={form.description} onChange={(value) => update("description", value)} testId="admin-event-description-textarea" required />
        <SubmitButton pending={mutation.isPending} label={editing ? "Guardar evento" : "Publicar evento"} testId="admin-event-submit-button" />
      </form>
      <AdminItemList queryKey="events" endpoint="events" title="Eventos" onEdit={setEditing} getTitle={(item) => item.title} getMeta={(item) => `${item.location} · ${item.modality}`} />
    </>
  );
}

const CHRONICLE_CATEGORIES = ["Crónica", "Cartel", "Sorteo de escuadras", "General"];

function ChronicleForm({ user }) {
  const staff = user?.role === "admin" || user?.role === "editor";
  const allowedCategories = staff ? CHRONICLE_CATEGORIES : CHRONICLE_CATEGORIES.filter((category) => (category === "Cartel" ? user?.can_publish_posters : category === "Crónica" ? user?.can_publish_chronicles : user?.can_publish_posters || user?.can_publish_chronicles));
  const initial = { title: "", excerpt: "", content: "", date: today, location: "", category: allowedCategories.includes("Crónica") ? "Crónica" : allowedCategories[0] || "Crónica", front_until: "", modality: "Foso Universal", image: "", photos: [], tags: "Crónica, Premiados", winner: "", score: "", series: "", classifications_text: "", social_summary: "", flickr_url: "", flickr_albums_text: "", publish_social: true };
  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(null);
  const mutation = usePublisher("chronicles", ["chronicles", "admin-chronicles", "stats"], staff ? (editing ? "Publicación actualizada" : "Publicada correctamente") : "Enviada para revisión: la redacción la aprobará en breve");
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (editing) {
      setForm({ ...editing, category: editing.category || "Crónica", front_until: editing.front_until || "", image: editing.image || "", photos: editing.photos || [], score: editing.score == null ? "" : String(editing.score), series: (editing.series || []).join(", "), tags: editing.tags.join(", "), modality: editing.modality || "Foso Universal", classifications_text: classificationsToText(editing.classifications || []), flickr_albums_text: albumsToText(editing.flickr_albums || []) });
    }
  }, [editing]);

  const reset = () => {
    setEditing(null);
    setForm(initial);
  };

  const submit = (event) => {
    event.preventDefault();
    const { flickr_albums_text, classifications_text, slug, social, created_at, status, author_id, review_note, ...rest } = form;
    rest.front_until = rest.front_until || null;
    const series = parseSeries(form.series);
    const total = series.reduce((sum, value) => sum + value, 0);
    const score = Number(form.score) || total || null;
    const parsed = parseClassifications(classifications_text);
    const first = { position: 1, name: form.winner, category: "Absoluta", score, series };
    const classifications = parsed.length ? parsed : isResults && form.winner ? [first] : [];
    mutation.mutate({
      id: editing?.id,
      payload: {
        ...rest,
        winner: isResults ? form.winner : "",
        score: isResults ? score : null,
        series: isResults ? series : [],
        classifications: isResults ? classifications : [],
        tags: form.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
        flickr_albums: parseFlickrAlbums(flickr_albums_text),
      },
    }, { onSuccess: reset });
  };

  const isResults = form.category === "Crónica";

  return (
    <>
      <form onSubmit={submit} className="admin-form" data-testid="admin-chronicle-form">
        {editing ? <EditNotice title={editing.title} onCancel={reset} testId="admin-chronicle-cancel-edit" /> : null}
        <Field label="Titular" value={form.title} onChange={(value) => update("title", value)} testId="admin-chronicle-title-input" required />
        <TextareaField label="Entradilla" value={form.excerpt} onChange={(value) => update("excerpt", value)} testId="admin-chronicle-excerpt-textarea" required />
        <TextareaField label="Crónica completa" value={form.content} onChange={(value) => update("content", value)} testId="admin-chronicle-content-textarea" required className="min-h-40" />
        <div className="grid gap-5 md:grid-cols-4">
          <SelectField label="Categoría" value={form.category} onChange={(value) => update("category", value)} options={allowedCategories} testId="admin-chronicle-category-select" />
          <DateField label={isResults ? "Fecha de la tirada (ya disputada)" : "Fecha"} value={form.date} onChange={(value) => update("date", value)} testId="admin-chronicle-date-input" max={isResults ? today : undefined} required />
          <Field label="Ubicación" value={form.location} onChange={(value) => update("location", value)} testId="admin-chronicle-location-input" required />
          <SelectField label="Modalidad" value={form.modality} onChange={(value) => update("modality", value)} options={modalities} testId="admin-chronicle-modality-select" />
        </div>
        {isResults ? (
          <>
            <div className="grid gap-5 md:grid-cols-3">
              <Field label="Ganador/a" value={form.winner} onChange={(value) => update("winner", value)} testId="admin-chronicle-winner-input" required />
              <Field label="Platos del ganador/a" type="number" value={form.score} onChange={(value) => update("score", value)} testId="admin-chronicle-score-input" placeholder="Opcional" />
              <Field label="Series del ganador/a" value={form.series} onChange={(value) => update("series", value)} testId="admin-chronicle-series-input" placeholder="24, 25, 23, 24" />
            </div>
            <TextareaField label="Clasificación completa (una línea por tirador: Nombre | Categoría | series separadas por espacios)" value={form.classifications_text} onChange={(value) => update("classifications_text", value)} testId="admin-chronicle-classifications-textarea" className="min-h-36 font-mono text-sm" placeholder="Joan Alcoba | Absoluta | 24 25 23 24&#10;José Huerva | 1ª Categoría | 23 24 23 24&#10;Miguel Lacoma | Veteranos | 22 23 24 23" />
          </>
        ) : null}
        <Field label="Etiquetas separadas por comas" value={form.tags} onChange={(value) => update("tags", value)} testId="admin-chronicle-tags-input" required />
        <ChroniclePhotoField form={form} update={update} />
        <ChronicleGalleryField form={form} update={update} />
        {staff ? (
          <>
            <Field label="URL del álbum principal de Flickr" value={form.flickr_url} onChange={(value) => update("flickr_url", value)} testId="admin-chronicle-flickr-input" placeholder="https://www.flickr.com/photos/tu-cuenta/albums/..." />
            <TextareaField label="Álbumes Flickr por localidad" value={form.flickr_albums_text} onChange={(value) => update("flickr_albums_text", value)} testId="admin-chronicle-flickr-albums-textarea" className="min-h-28" placeholder="Zaidín | https://www.flickr.com/photos/cuenta-zaidin/albums/...&#10;Binéfar | https://www.flickr.com/photos/cuenta-binefar/albums/..." />
          </>
        ) : null}
        <TextareaField label="Resumen para redes y prensa" value={form.social_summary} onChange={(value) => update("social_summary", value)} testId="admin-chronicle-social-textarea" required />
        <DateField label="Mostrar en portada hasta (opcional)" value={form.front_until} onChange={(value) => update("front_until", value)} testId="admin-chronicle-front-until-input" min={form.date || undefined} clearable hint="Sorteos y noticias generales desaparecen de la portada tras su fecha si no indicas otra. Las crónicas y carteles permanecen salvo que pongas una fecha." />
        {staff ? <SocialPublishField checked={form.publish_social} onChange={(value) => update("publish_social", value)} editing={editing} /> : null}
        <SubmitButton pending={mutation.isPending} label={editing ? "Guardar" : isResults ? "Publicar crónica" : "Publicar noticia"} testId="admin-chronicle-submit-button" />
      </form>
      <AdminItemList queryKey="admin-chronicles" fetchPath="/admin/chronicles" invalidate={["chronicles"]} endpoint="chronicles" title={staff ? "Publicaciones" : "Mis publicaciones"} eyebrow={staff ? "Publicadas y pendientes" : "Enviadas (pendientes, publicadas o rechazadas)"} onEdit={setEditing} getTitle={(item) => item.title} getMeta={(item) => `${item.category || "Crónica"} · ${item.location} · ${item.modality || ""}${item.winner ? ` · ${item.winner}` : ""}`} />
    </>
  );
}

function CalendarForm() {
  const initial = { title: "", date: "2026-08-16", month: "Agosto", location: "", province: "", modality: "Foso Universal", status: "Programada", targets: "", description: "", poster_url: "", results_url: "" };
  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(null);
  const mutation = usePublisher("calendar", ["calendar"], editing ? "Tirada actualizada" : "Tirada añadida al calendario");
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (editing) {
      setForm({ ...editing, targets: editing.targets ? String(editing.targets) : "", description: editing.description || "", poster_url: editing.poster_url || "", results_url: editing.results_url || "" });
    }
  }, [editing]);

  const reset = () => {
    setEditing(null);
    setForm(initial);
  };

  const submit = (event) => {
    event.preventDefault();
    const payload = { ...form, targets: form.targets ? Number(form.targets) : null, results_url: form.results_url || null, results_links: form.results_url ? [{ label: "Resultados", url: form.results_url }] : [] };
    mutation.mutate({ id: editing?.id, payload }, { onSuccess: reset });
  };

  return (
    <>
      <form onSubmit={submit} className="admin-form" data-testid="admin-calendar-form">
        {editing ? <EditNotice title={editing.title} onCancel={reset} testId="admin-calendar-cancel-edit" /> : null}
        <Field label="Tirada" value={form.title} onChange={(value) => update("title", value)} testId="admin-calendar-title-input" required />
        <div className="grid gap-5 md:grid-cols-3">
          <DateField label="Fecha" value={form.date} onChange={(value) => update("date", value)} testId="admin-calendar-date-input" required />
          <SelectField label="Mes" value={form.month} onChange={(value) => update("month", value)} options={months} testId="admin-calendar-month-select" />
          <SelectField label="Modalidad" value={form.modality} onChange={(value) => update("modality", value)} options={modalities} testId="admin-calendar-modality-select" />
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          <Field label="Ubicación" value={form.location} onChange={(value) => update("location", value)} testId="admin-calendar-location-input" required />
          <Field label="Provincia" value={form.province} onChange={(value) => update("province", value)} testId="admin-calendar-province-input" required />
          <Field label="Estado" value={form.status} onChange={(value) => update("status", value)} testId="admin-calendar-status-input" required />
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          <Field label="Platos" type="number" value={form.targets} onChange={(value) => update("targets", value)} testId="admin-calendar-targets-input" />
          <Field label="URL del cartel" value={form.poster_url} onChange={(value) => update("poster_url", value)} testId="admin-calendar-poster-input" placeholder="https://..." />
          <Field label="URL de resultados" value={form.results_url} onChange={(value) => update("results_url", value)} testId="admin-calendar-results-input" placeholder="https://..." />
        </div>
        <TextareaField label="Descripción" value={form.description} onChange={(value) => update("description", value)} testId="admin-calendar-description-textarea" className="min-h-24" />
        <SubmitButton pending={mutation.isPending} label={editing ? "Guardar tirada" : "Añadir al calendario"} testId="admin-calendar-submit-button" />
      </form>
      <AdminItemList queryKey="calendar" endpoint="calendar" title="Calendario" onEdit={setEditing} getTitle={(item) => item.title} getMeta={(item) => `${item.location} · ${item.modality}`} />
    </>
  );
}

function EditNotice({ title, onCancel, testId }) {
  return (
    <div className="flex flex-col justify-between gap-4 border border-clay/35 bg-clay/10 p-4 sm:flex-row sm:items-center" data-testid="admin-edit-notice">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay">Editando contenido</p>
        <p className="mt-1 text-sm font-semibold text-slate-100">{title}</p>
      </div>
      <Button type="button" onClick={onCancel} variant="outline" className="rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.18em] hover:border-clay/50 hover:bg-clay/10 hover:text-clay" data-testid={testId}>
        Cancelar edición
      </Button>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text", ...props }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <Input type={type} value={value} onChange={(event) => onChange(event.target.value)} className="h-12 rounded-none border-white/10 bg-charcoal text-slate-100 placeholder:text-slate-600" data-testid={testId} {...props} />
    </label>
  );
}

function TextareaField({ label, value, onChange, testId, className = "min-h-28", ...props }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <Textarea value={value} onChange={(event) => onChange(event.target.value)} className={`${className} rounded-none border-white/10 bg-charcoal text-slate-100 placeholder:text-slate-600`} data-testid={testId} {...props} />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-12 rounded-none border-white/10 bg-charcoal text-slate-100" data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="border-white/10 bg-panel text-slate-100">
          {options.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
        </SelectContent>
      </Select>
    </label>
  );
}

function SubmitButton({ pending, label, testId }) {
  return (
    <Button disabled={pending} className="h-12 rounded-none bg-clay px-8 font-mono text-xs uppercase tracking-[0.22em] text-charcoal hover:bg-brass" data-testid={testId}>
      {pending ? "Publicando..." : label}
    </Button>
  );
}
